import { Link } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import {
  formatMoney,
  productImageAlt,
  shopifyImg,
  shopifySrcSet,
  type ShopifyProduct,
} from "@/lib/shopify";
import { useCartStore } from "@/stores/cartStore";
import { displayBrand, getProductFacts, verifiedAge } from "@/lib/product-facts";
import { ga4ItemCategory, trackRailInteraction, trackSelectItem } from "@/lib/gtag";
import { useT, useMarket } from "@/i18n";

/**
 * Cleans visual noise from titles for grid display while keeping
 * the full SEO title on the product detail page.
 */
function displayTitle(title: string) {
  return title
    .replace(/\s*[-–—|]\s*Montessori24.*$/i, "")
    .replace(/\s*\([^)]{0,40}\)\s*$/g, "")
    .trim();
}

export interface ProductCardProps {
  product: ShopifyProduct;
  /** Image sizes attribute, defaults to a 3-col grid */
  sizes?: string;
  /** Hide the quick add-to-cart button (e.g. in carousels) */
  showAddToCart?: boolean;
  /** Analytics: rail this card belongs to (e.g. "pikler", "iglu") */
  railName?: string;
  /** Analytics: index within the rail */
  railPosition?: number;
  /**
   * Show the extended homepage meta block: verified age recommendation,
   * a short benefit line and the store-wide delivery promise. Only verified
   * facts are rendered — nothing is invented when the data is missing.
   */
  showMeta?: boolean;
  /** Short benefit line (homepage curated grids only). */
  benefit?: string;
}

export function ProductCard({
  product,
  sizes = "(min-width: 1024px) 320px, (min-width: 640px) 45vw, 90vw",
  showAddToCart = true,
  railName,
  railPosition,
  showMeta = false,
  benefit,
}: ProductCardProps) {
  const node = product.node;
  const img = node.images.edges[0]?.node;
  const variant = node.variants.edges[0]?.node;
  const price = node.priceRange.minVariantPrice;
  const addItem = useCartStore((s) => s.addItem);
  const isLoading = useCartStore((s) => s.isLoading);
  const t = useT();
  const market = useMarket();
  // Only render an age label when the merchant's own product data states one.
  const ageLabel = showMeta ? verifiedAge(getProductFacts(node.handle)) : undefined;

  const handleAdd = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!variant) return;
    await addItem({
      product,
      variantId: variant.id,
      variantTitle: variant.title,
      price: variant.price,
      quantity: 1,
      selectedOptions: variant.selectedOptions || [],
    });
  };

  return (
    <Link
      to="/product/$handle"
      params={{ handle: node.handle }}
      search={market === "us" ? { market: "us" } : undefined}
      className="group block rounded-[8px] focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground/60 focus-visible:ring-offset-4 focus-visible:ring-offset-background"
      onClick={() => {
        trackSelectItem({
          id: variant?.id ?? node.id,
          name: node.title,
          brand: displayBrand(node.vendor),
          category: ga4ItemCategory(node.title),
          price: parseFloat(price.amount),
          currency: price.currencyCode,
          listName: railName,
          index: railPosition,
        });
        if (railName) {
          trackRailInteraction({
            rail: railName,
            action: "product_click",
            product_id: node.id,
            product_name: node.title,
            position: railPosition,
          });
        }
      }}
    >
      <div className="product-frame relative overflow-hidden rounded-[8px] border border-border/60 transition-all duration-500 ease-out will-change-transform group-hover:-translate-y-[3px] group-hover:shadow-[0_18px_40px_-28px_rgb(41_43_40/0.35)] group-focus-within:-translate-y-[3px] motion-reduce:transform-none motion-reduce:transition-none">
        {img && (
          <img
            src={shopifyImg(img.url, 500)}
            srcSet={shopifySrcSet(img.url, 500)}
            sizes={sizes}
            width={500} height={500} alt={productImageAlt(img.altText, node.title)}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover object-center transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.025] group-focus-within:scale-[1.025] motion-reduce:transform-none motion-reduce:transition-none"
          />
        )}
        {!variant?.availableForSale && (
          <span className="absolute left-3 top-3 rounded-[4px] bg-foreground/85 px-2.5 py-1 text-[10px] uppercase tracking-[0.14em] text-background backdrop-blur">
            {t("product.soldOut")}
          </span>
        )}
        {showAddToCart && variant?.availableForSale && (
          <button
            type="button"
            onClick={handleAdd}
            disabled={isLoading}
            aria-label={`${t("product.addToCart")}: ${displayTitle(node.title)}`}
            className="absolute inset-x-3 bottom-3 hidden min-h-[44px] rounded-[6px] bg-foreground/95 px-4 py-3 text-[13px] font-medium tracking-wide text-background backdrop-blur transition-all duration-500 ease-out sm:block sm:translate-y-3 sm:opacity-0 sm:group-hover:translate-y-0 sm:group-hover:opacity-100 sm:group-focus-within:translate-y-0 sm:group-focus-within:opacity-100 focus-visible:translate-y-0 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-background/70 focus-visible:ring-offset-2 focus-visible:ring-offset-foreground disabled:cursor-not-allowed [@media(hover:none)]:sm:opacity-100 [@media(hover:none)]:sm:translate-y-0"
          >
            {isLoading ? (
              <Loader2 className="mx-auto h-4 w-4 animate-spin" />
            ) : (
              t("product.addToCart")
            )}
          </button>
        )}
      </div>
      <div className="mt-4 px-0.5">
        {showMeta && ageLabel ? (
          <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-clay">
            {ageLabel}
          </p>
        ) : null}
        <h3
          className={`text-[15px] leading-snug text-foreground line-clamp-2 min-h-[2.6em] ${
            showMeta && ageLabel ? "mt-1.5" : ""
          }`}
        >
          {displayTitle(node.title)}
        </h3>
        {showMeta && benefit ? (
          <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground line-clamp-2">
            {benefit}
          </p>
        ) : null}
        <div className="mt-2 flex items-baseline justify-between gap-3">
          <span className="text-sm tracking-wide text-foreground">
            {formatMoney(price.amount, price.currencyCode)}
          </span>
          <span
            aria-hidden
            className="text-[12px] font-medium text-foreground/55 opacity-0 transition-opacity duration-300 group-hover:opacity-100 group-focus-within:opacity-100"
          >
            Katso tuote →
          </span>
        </div>
        {showMeta ? (
          <p className="mt-2 text-[12px] text-muted-foreground">
            Ilmainen toimitus · 3–7 arkipäivää
          </p>
        ) : null}
      </div>
    </Link>
  );
}